import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog'
import { faEdit, faPlus, faUsers, faWarning } from '@fortawesome/free-solid-svg-icons';
import { MatTableDataSource } from '@angular/material/table';
import { AssessmentService } from 'src/app/services/assessment.service';
import { Observable, map, of, startWith } from 'rxjs';
import { FormControl } from '@angular/forms';
import { DialogService } from 'src/app/services/dialog.service';
import { Title } from '@angular/platform-browser';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserService } from 'src/app/services/user.service';
import { ComplexityModalComponent } from '../../fire-tracker/complexity-modal/complexity-modal.component';
import { EditAssessmentComponent } from '../edit-assessment/edit-assessment.component';
import { RequestSupportComponent } from '../request-support/request-support.component';
import { SupportService } from 'src/app/services/support.service';
import { ReportService } from 'src/app/services/report.service';

@Component({
    selector: 'app-support-requests-tab',
    templateUrl: './support-requests-tab.component.html',
    styleUrls: ['./support-requests-tab.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})


export class SupportRequestsTabComponent implements OnInit {
  //icons
  faEdit = faEdit;
  faUsers = faUsers;
  faPlus = faPlus;
  faWarning = faWarning;

  //table variables
  pageNumber = 0;
  pageSize = 10;
  length = 0;
  sort = "updated_on"
  sortDirection = "desc"
  selectedForestFlag = false;
  supports = new MatTableDataSource<any>();
  expandedAssessments: any;
  displayedColumns!: string[];

  //dropdown lists
  regions: any;
  statuses: any;
  complexities: any;
  team_leads: any;

  //access
  isTeamLead: boolean = false;
  isCo_or: boolean = false;
  isForestCo_or: boolean = false;
  user: any;

  //forest name filter
  forestNameList: string[];
  filtered_forest_names: Observable<string[]>;
  @ViewChild('forest') input?: ElementRef<HTMLInputElement>;
  forest_name = new FormControl("")
  selectedRegion: string | undefined = undefined;

  @Input() assessmentData: any;
  @Input() updateAssessment: any;

  //search bar items
  filters = {
    region: null,
    forest_name: null as (string | null),
    baer_name: null,
    assessment_status_id: null,
    assessment_status_ids: [1, 3, 4],
    fire_complexity_id: null,
    team_lead: null,
    support_completed: false as (boolean | undefined),
  }
  filters_clean = true;

  constructor(
    public dialog: MatDialog,
    private readonly assessmentService: AssessmentService,
    private readonly supportService: SupportService,
    private readonly dialogService: DialogService,
    private readonly titleService: Title,
    private readonly userService: UserService,
    private readonly reportService: ReportService
  ) {
    this.titleService.setTitle("BAER - Assessments")
    this.regions = this.assessmentService.getRegionList();
    this.statuses = this.assessmentService.getStatusList().filter((x: any) => [2, 4, 5, 12].includes(x.value));;
    this.complexities = this.assessmentService.getComplexityList();
    this.forestNameList = this.assessmentService.getForestNameList();
    this.filtered_forest_names = of(this.forestNameList)

    this.assessmentService.finishLoadingSubscription().subscribe(() => {
      this.regions = this.assessmentService.getRegionList();
      this.statuses = this.assessmentService.getStatusList().filter((x: any) => [2, 4, 5, 12].includes(x.value));
      this.complexities = this.assessmentService.getComplexityList();
      this.forestNameList = this.assessmentService.getForestNameList(this.filters.region as any);

      this.filtered_forest_names = of(this.forestNameList)
      this.filtered_forest_names = this.forest_name.valueChanges.pipe(
        startWith(""),
        map((value) => this._filter((value as string) || ""))
      )
    })
  }

  regionChanged(): void {
    this.forestNameList = this.assessmentService.getForestNameList(this.filters.region as any);
    this.filtered_forest_names = this.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  ngOnInit(): void {
    this.filters_clean = false;
    this.loadUser();

    this.displayedColumns = [
      'baer_name',
      'region',
      'support_status',
      'support_needed_type',
      'support_skills',
      'support_details',
      'actions'
    ]
    this.filtered_forest_names = this.forest_name.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter((value as string) || ""))
    )
  }

  loadUser() {
    this.userService.getUserProfile().then(async (user) => {
      this.user = user;
      if (this.user.isWO) {
        this.loadAssessments();
      } else if (user.isCO) {
        this.isCo_or = true;
        this.loadAssessments();
      } else if (user.isFOCO) {
        this.isForestCo_or = true;
        this.filters.region = user.region;
        this.forest_name.setValue(user.forest_name);
        this.filters_clean = false;
        this.search();
      } else if (user.teamLeadRoles) { //keep highest privilage
        this.isTeamLead = true;
        this.filters_clean = false;
        this.loadAssessments();
      } else {
        this.filters.region = user.region;
        this.forest_name.setValue(user.forest_name);
        this.filters_clean = false;
        this.search();
      }
    });
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase()
    return this.forestNameList.filter((option: string) =>
      option.toLowerCase().includes(filterValue)
    )
  }
  filter(): void {
    const filterValue = this.input?.nativeElement.value.toLowerCase() ?? '';
    this.filtered_forest_names = of(this._filter(filterValue));
  }

  search(): void {
    this.filters_clean = false;
    this.pageNumber = 0;
    this.loadAssessments();
  }

  clearSearch(): void {
    this.filters_clean = true;
    this.pageNumber = 0;
    this.filters = {
      region: null,
      forest_name: null,
      baer_name: null,
      assessment_status_id: null,
      assessment_status_ids: [1, 3, 4],
      fire_complexity_id: null,
      team_lead: null,
      support_completed: undefined,
    }
    this.forest_name.setValue("");
    this.regionChanged();
    this.loadAssessments();
  }

  pageEvent(e: any) {
    this.pageNumber = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadAssessments();
  }

  async loadAssessments() {
    this.dialogService.openSpinner()
    this.filters.forest_name = this.forest_name.value ?? null;
    this.supportService.getSupportList(this.pageNumber + 1, this.pageSize, this.filters, this.sort, this.sortDirection).subscribe({//this.filters).subscribe({
      next: (data) => {
        this.supports.data = data.supports;
        this.length = data.totalResults;
        this.supports.data.forEach((assessment: any) => {
          if (assessment.support_skills) {
            let skills = assessment.support_skills.split(", ");
            assessment.main_skill = skills[0];
            skills.shift();
            assessment.support_skills = skills.join("\n");
            assessment.other_skill_count = skills.length;
          }
        })

        this.supports.data.forEach((assessment: any) => {
          if (assessment.assessment_id)
            this.loadReportData(assessment.assessment);
        });
      },
      complete: () => {
        this.dialogService.closeSpinner();
        return true;
      },
      error: (err) => {
        return false;
      }
    })
  }

  loadReportData(assessment: any) {
    let reportRelations = '&relations=users.skills,users.user.skills,assessment,supporting_files,supports,soil_burn_severities,comments,forests,report_status';
    this.reportService.getReportList(assessment.assessment_id, reportRelations).subscribe(
      {
        next: (data) => {
          let reports = data.reports;
          reports.sort((reportA: any, reportB: any) => reportA.report_num - reportB.report_num);
          assessment.reports = reports;
        }
      });
  }

  sortChange(sortState: any) {
    this.sort = sortState.active;
    this.sortDirection = sortState.direction;
    this.pageNumber = 0;
    this.loadAssessments()
  }

  clearValue(event: any) {
    event.target.value = ""
  }

  clearForest() {
    if (!this.selectedForestFlag) {
      this.forest_name.setValue("")
    }
    this.selectedForestFlag = false
  }

  forestSelected() {
    this.selectedForestFlag = true
  }

  complexityModal(): void {
    this.dialog.open(ComplexityModalComponent, {
      width: "700px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
    })
  }

  editBaerTeamNeeds(support: any): void {
    if (support.assessment) {
      this.dialog.open(EditAssessmentComponent, {
        maxWidth: "1000px",
        panelClass: ['animate__animated', 'animate__fadeInDown'],
        data: {
          fireRecord: support.assessment,
          isTeamLead: this.isTeamLead,
          user: this.user
        },
      }).afterClosed().subscribe((reload) => {
        this.titleService.setTitle("BAER - Assessments")
        if (reload) {
          this.loadAssessments();
        }
      })
    } else {
      this.dialogService.openSpinner();
      this.assessmentService.getAssessmentByID(support.report.assessment_id).subscribe({
        next: (data) => {
          this.dialog.open(EditAssessmentComponent, {
            maxWidth: "900px",
            panelClass: ['animate__animated', 'animate__fadeInDown'],
            data: {
              fireRecord: data,
              isTeamLead: this.isTeamLead,
              user: this.user
            },
          }).afterClosed().subscribe((reload) => {
            this.titleService.setTitle("BAER - Assessments")
            if (reload) {
              this.loadAssessments();
            }
          })
        },
        complete: () => {
          this.dialogService.closeSpinner();
          return true;
        },
        error: (err) => {
          return false;
        }
      })
    }
  }

  requestSupport(assessment: any): void {
    this.dialog.open(RequestSupportComponent, {
      maxWidth: "900px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: assessment,
      },
    }).afterClosed().subscribe((reload) => {
      this.titleService.setTitle("BAER - Assessments")
      if (reload) {
        this.loadAssessments();
      }
    })
  }

  stopPropagation(event: any) {
    event.stopPropagation();
  }
}
